import { lerpRadians } from "common/utils/math";
import noop from "common/utils/noop";
import stableHash from "common/utils/stableHash";
import newUUID from "common/utils/uuid";
import type { AABB2 } from "geom/aabb2Types";
import type { PlaneBSP } from "geom/bspTypes";
import * as THREE from "three";
import { Plane as THREEPlane } from "three";
import { defaultOrthoCameraSettings, type CameraSettings } from "./CameraSettings";
import type { CanvasControls } from "./CanvasControls";
import { cameraToTHREE } from "./convert";
import { defaultStencilProperties } from "./defaultStencilProperties";
import type { HighlightDataTexture } from "./HighlightDataTexture";
import { includeEntityHighlight } from "./materials/customizeShader";
import type { HatchMaterialOptions } from "./materials/hatchMaterial";
import { HatchMaterialFactory } from "./materials/hatchMaterial";
import { LineBasicMaterialFactory } from "./materials/LineBasicMaterialFactory";
import type { LineStrokeMaterial, LineStrokeMaterialOptions } from "./materials/lineStrokeMaterial";
import { LineStrokeMaterialFactory } from "./materials/lineStrokeMaterial";
import { MeshBasicMaterialFactory } from "./materials/MeshBasicMaterialFactory";
import OrthographicControls, { orthoSphericalToViewDirection } from "./OrthographicControls";
import PerspectiveControls from "./PerspectiveControls";
import type { SharedWebGL } from "./SharedWebGL";
import type { THREECamera } from "./THREECamera";
import { THREERaycastHelper } from "./THREERaycastHelper";
import type { THREEViewerRendererForBase } from "./THREEViewerBaseTypes";
import { THREEViewerScene } from "./THREEViewerScene";
import { WebGLMultiContextResourceTracker } from "./WebGLContextResourceTracker";

export interface THREEViewerBaseOptions {
  animate: boolean;
  cameraSettings: CameraSettings;
  highlightDataTexture?: HighlightDataTexture;
  cameraIgnoreLeftClickWithoutCtrl?: boolean;
  darkMode?: boolean;
}

const tmpVector3 = new THREE.Vector3();

export abstract class THREEViewerBase {
  private readonly baseOptions: THREEViewerBaseOptions;

  renderOrder: number = 0; // Render order of the viewer relative to other viewers when using shared WebGL context, like a CSS z-index.

  private readonly isolateModeUniform: THREE.IUniform;
  private readonly highlightDataTextureUniform: THREE.IUniform;

  get highlightDataTexture() {
    return this.scene.highlightDataTexture;
  }

  set highlightDataTexture(value: HighlightDataTexture | undefined) {
    this.scene.highlightDataTexture = value;
    this.highlightDataTextureUniform.value = this.scene.highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = this.scene.highlightDataTexture?.isolateMode ?? false;
  }

  readonly scene: THREEViewerScene;

  protected autoRotate: boolean = false;
  protected readonly raycaster = new THREE.Raycaster();

  private readonly pointsMaterialCache: { [hash: string]: THREE.PointsMaterial } = {};

  cameraSettings: CameraSettings = defaultOrthoCameraSettings;
  camera: THREECamera = cameraToTHREE(defaultOrthoCameraSettings, 1);
  controls!: CanvasControls;
  protected disposeCtrls: () => void = noop;
  private transitionAnimationGeneration: number = 0;

  readonly name: string;

  private requestResizeListener: () => void;
  private disposed: boolean = false;

  private readonly raycastHelper = new THREERaycastHelper();

  private readonly cameraChangeListeners: (() => void)[] = [];
  private readonly controlsChangeListeners: ((controls: CanvasControls) => void)[] = [];

  public container: HTMLElement | null | undefined;
  public pixelRatio: number; // Pixel ratio used to render the viewer.

  abstract get viewerRenderer(): THREEViewerRendererForBase;

  private contextLostListenerCanvas: HTMLCanvasElement | undefined;
  private contextLostListener: (() => void) | undefined;

  setContextLostListener(canvas: HTMLCanvasElement, listener: () => void) {
    canvas.addEventListener("webglcontextlost", listener);
    this.contextLostListenerCanvas = canvas;
    this.contextLostListener = listener;
  }
  removeContextLostListener() {
    if (this.contextLostListenerCanvas && this.contextLostListener) {
      this.contextLostListenerCanvas.removeEventListener("webglcontextlost", this.contextLostListener);
      this.contextLostListenerCanvas = undefined;
      this.contextLostListener = undefined;
    }
  }

  protected sharedWebGL: SharedWebGL | null = null;

  setSharedWebGL(value: SharedWebGL | null) {
    if (this.sharedWebGL === value) {
      return;
    }

    if (!this.sharedWebGL && value) {
      // This should not normally happen.
      console.error(
        "Unexpected use of WebGL context sharing: Switching viewer from not sharing a WebGL context to sharing a WebGL context.",
      );
      this.removeContextLostListener();
      WebGLMultiContextResourceTracker.removeContext(this.viewerRenderer.renderer.getContext());
      this.viewerRenderer.renderer.forceContextLoss();
    }
    this.sharedWebGL = value;
    this.initViewerRenderer();
  }

  constructor({
    name,
    options,
    container,
    useSsfxRendererScene,
    pixelRatio,
  }: {
    name: string;
    options: THREEViewerBaseOptions;
    container: HTMLElement | null | undefined;
    useSsfxRendererScene: boolean;
    pixelRatio: number; // Pixel ratio used to render the viewer.
  }) {
    this.name = name;
    this.baseOptions = options;
    this.container = container;
    this.pixelRatio = pixelRatio;
    this.highlightDataTextureUniform = { value: options.highlightDataTexture?.texture ?? null };
    this.isolateModeUniform = { value: options.highlightDataTexture?.isolateMode ?? false };

    const scene = (this.scene = new THREEViewerScene(useSsfxRendererScene, options.darkMode ?? false));
    this.scene.highlightDataTexture = options.highlightDataTexture;
    scene.viewers.push(this);

    this.requestResizeListener = () => this.requestResize();
    window.addEventListener("resize", this.requestResizeListener, false);

    this.initializeControls(defaultOrthoCameraSettings);
  }

  protected getRenderPixelSize() {
    if (this.viewerRenderer) {
      return this.viewerRenderer.getRenderPixelSize();
    }
    return { renderPixelWidth: 1, renderPixelHeight: 1 };
  }

  protected getAspectRatio(): number {
    if (!this.viewerRenderer && this.container) {
      return Math.max(this.container.clientWidth, 1) / Math.max(this.container.clientHeight, 1);
    }
    const { renderPixelWidth, renderPixelHeight } = this.getRenderPixelSize();
    return renderPixelWidth / renderPixelHeight;
  }

  protected getPixelRatio(): number {
    return this.pixelRatio;
  }

  protected setPixelRatio(pixelRatio: number) {
    this.pixelRatio = pixelRatio;
    this.requestResize();
  }

  /**
   * useTHREEViewerSystem may re-host the viewer into a new parent element.
   */
  setContainer(container: HTMLElement | undefined) {
    if (this.controls && container) {
      this.controls.domElement = container;
    }
    this.container = container;
    this.viewerRenderer?.setContainer(container);
  }

  getLineStrokeMaterial(options: Partial<LineStrokeMaterialOptions>): LineStrokeMaterial {
    return LineStrokeMaterialFactory.instance.getMaterial(options);
  }

  getMeshBasicMaterial(options: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial {
    return MeshBasicMaterialFactory.instance.getMaterial(options);
  }

  getPointsMaterial(options: THREE.PointsMaterialParameters): THREE.PointsMaterial {
    const hash = stableHash(options);
    if (this.pointsMaterialCache[hash]) {
      return this.pointsMaterialCache[hash];
    }

    const material = new THREE.PointsMaterial({
      ...defaultStencilProperties,
      ...options,

      // The transparent flag affects render order, so we always set 2D objects as transparent.
      transparent: true,
      // PointsMaterial size needs to be scaled by the pixel ratio as we render them in a RenderTarget
      // where the devicePixelRatio is 1. See: https://github.com/mrdoob/three.js/issues/7451
      size: (options.size ?? 1) * this.pixelRatio,
    });

    material.defines = { ISOLATE_MODE: 1 };

    material.onBeforeCompile = (shader) => {
      const { uniforms } = shader;

      uniforms.isolateMode = this.isolateModeUniform;
      uniforms.highlightedIds = this.highlightDataTextureUniform;

      return includeEntityHighlight(shader, true);
    };

    return (this.pointsMaterialCache[hash] = material);
  }

  getLineBasicMaterial(options: THREE.LineBasicMaterialParameters): THREE.LineBasicMaterial {
    return LineBasicMaterialFactory.instance.getMaterial(options);
  }

  getLineDashedMaterial(options: THREE.LineDashedMaterialParameters): THREE.LineBasicMaterial {
    return LineBasicMaterialFactory.instance.getDashedMaterial(options);
  }

  getMeshHatchMaterial(options: Partial<HatchMaterialOptions>): THREE.ShaderMaterial {
    return HatchMaterialFactory.instance.getMaterial(options);
  }

  protected initializeControls(cameraSettings: CameraSettings): void {
    const { targetX, targetY, targetZ } = cameraSettings;
    if (cameraSettings.ortho) {
      const controls = new OrthographicControls(this.camera, this.container, targetX, targetY, targetZ);
      this.setControls(controls);
    } else {
      const controls = new PerspectiveControls(this.camera, this.container, targetX, targetY, targetZ);
      this.setControls(controls);
    }
  }

  // Called when initialization fails to clean up the listener.
  protected removeResizeListener() {
    window.removeEventListener("resize", this.requestResizeListener, false);
  }

  dispose(): boolean {
    // only dispose once
    if (this.disposed) {
      return false;
    }

    this.disposed = true;
    if (this.disposeCtrls) {
      this.disposeCtrls();
    }
    this.viewerRenderer?.dispose();
    this.removeResizeListener();
    return true;
  }

  setControls(ctrls: CanvasControls) {
    if (this.disposeCtrls) {
      this.disposeCtrls();
    }

    this.controls = ctrls;
    ctrls.autoRotate = this.autoRotate;
    ctrls.ignoreLeftClickWithoutCtrl = this.baseOptions.cameraIgnoreLeftClickWithoutCtrl ?? false;

    const controlsChange = () => this.markRendererForUpdate();
    this.controls.addEventListener("change", controlsChange);

    this.disposeCtrls = () => {
      this.controls.removeEventListener("change", controlsChange);
      this.controls.dispose();
    };

    ctrls.update();
    this.onControlsChange(ctrls);
  }

  setCameraSettings(cameraSettings: CameraSettings, viewerAspect?: number) {
    if (this.disposeCtrls) {
      this.disposeCtrls();
    }

    this.cameraSettings = cameraSettings;
    if (viewerAspect === undefined) {
      viewerAspect = this.getAspectRatio();
    }
    this.camera = cameraToTHREE(cameraSettings, viewerAspect);
    this.camera.getWorldDirection(tmpVector3);
    this.markRendererForUpdate();
  }

  setZoom(value: number) {
    this.camera.zoom = Math.max(this.controls.minZoom, Math.min(this.controls.maxZoom, value));
    this.camera.updateProjectionMatrix();
    this.controls.update();
    this.markRendererForUpdate();
  }

  private preTransitionConstraints: {
    minDistance: number;
    maxDistance: number;
    maxPolarAngle: number;
    enablePan: boolean;
  } | null = null;

  focusOnPoint(
    targetEndX: number,
    targetEndY: number,
    targetEndZ: number,
    cameraEndX: number,
    cameraEndY: number,
    cameraEndZ: number,
    endCameraNear?: number,
    endCameraFar?: number,
    endCameraFov?: number,
    durationFrames: number = 11,
  ) {
    endCameraNear = endCameraNear !== undefined ? endCameraNear : this.camera.near;
    endCameraFar = endCameraFar !== undefined ? endCameraFar : this.camera.far;
    endCameraFov = endCameraFov !== undefined ? endCameraFov : (this.camera as THREE.PerspectiveCamera).fov;

    const { x: cameraStartX, y: cameraStartY, z: cameraStartZ } = this.camera.position.clone();
    const startCameraNear = this.camera.near;
    const startCameraFar = this.camera.far;
    const startCameraFov = (this.camera as THREE.PerspectiveCamera).fov;

    const cameraStartQuaternion = new THREE.Quaternion();
    this.camera.getWorldQuaternion(cameraStartQuaternion);
    const cameraStartRotation = new THREE.Euler().setFromQuaternion(cameraStartQuaternion, "XZY");

    const startDistance = this.camera.position.distanceTo(this.controls.target);

    const cameraEndDirection = new THREE.Vector3(
      targetEndX - cameraEndX,
      targetEndY - cameraEndY,
      targetEndZ - cameraEndZ,
    );
    const endDistance = cameraEndDirection.length();
    const cameraEndLookAt = new THREE.Matrix4().lookAt(
      new THREE.Vector3(cameraEndX, cameraEndY, cameraEndZ),
      new THREE.Vector3(targetEndX, targetEndY, targetEndZ),
      new THREE.Vector3(0, 0, 1),
    );
    const cameraEndRotation = new THREE.Euler().setFromRotationMatrix(cameraEndLookAt, "XZY");

    // suspend constraints for transition
    const { minDistance, maxDistance, maxPolarAngle, enablePan } = this.controls;
    if (this.preTransitionConstraints === null) {
      this.preTransitionConstraints = { minDistance, maxDistance, maxPolarAngle, enablePan };
    }
    this.controls.minDistance = 0;
    this.controls.maxDistance = Infinity;
    const lockId = `focusOnPoint-${newUUID()}`;
    this.controls.lock(lockId);
    this.controls.update();

    // Cancel any previous transition animation so overlapping calls don't corrupt saved state.
    const generation = ++this.transitionAnimationGeneration;

    const smoothstep = (t) => t * t * (3.0 - 2.0 * t);

    let FOCUS_CAMERA_SPEED: number;
    let t: number;

    if (durationFrames === 0) {
      // setup camera and lookAt for teleport
      FOCUS_CAMERA_SPEED = 1;
      t = 1.0;
    } else {
      // setup camera and lookAt for in-betweening
      FOCUS_CAMERA_SPEED = 1 / durationFrames;
      t = FOCUS_CAMERA_SPEED;
    }

    const currentRotation = new THREE.Euler();
    const currentTargetOffset = new THREE.Vector3();

    // camera/lookat 'inbetweening'
    const move = () => {
      // If a newer transition animation has started, abandon this animation.
      // The newer animation will restore the constraints.
      if (generation !== this.transitionAnimationGeneration) {
        this.controls.unlock(lockId);
        return;
      }

      if (this.camera.type === "PerspectiveCamera") {
        const x = smoothstep(Math.min(t, 1.0));

        const x1 = 1.0 - x;

        this.camera.position.set(
          cameraStartX * x1 + x * cameraEndX,
          cameraStartY * x1 + x * cameraEndY,
          cameraStartZ * x1 + x * cameraEndZ,
        );

        currentRotation.set(
          lerpRadians(cameraStartRotation.x, cameraEndRotation.x, x),
          lerpRadians(cameraStartRotation.y, cameraEndRotation.y, x),
          lerpRadians(cameraStartRotation.z, cameraEndRotation.z, x),
          "XZY",
        );
        const currentTargetDistance = startDistance * x1 + x * endDistance;

        currentTargetOffset.set(0, 0, -currentTargetDistance);
        currentTargetOffset.applyEuler(currentRotation);

        this.controls.target.copy(this.camera.position);
        this.controls.target.add(currentTargetOffset);

        this.controls.update();
        this.camera.near = startCameraNear * x1 + x * endCameraNear;
        this.camera.far = startCameraFar * x1 + x * endCameraFar;
        (this.camera as THREE.PerspectiveCamera).fov = startCameraFov * x1 + x * endCameraFov;
        this.camera.updateProjectionMatrix();
        this.markRendererForUpdate();

        if (t >= 1.0) {
          const toRestore = this.preTransitionConstraints;
          if (toRestore) {
            this.controls.minDistance = toRestore.minDistance;
            this.controls.maxDistance = toRestore.maxDistance;
            this.controls.maxPolarAngle = toRestore.maxPolarAngle;
            this.controls.enablePan = toRestore.enablePan;
            this.preTransitionConstraints = null;
          }
          this.controls.unlock(lockId);
          this.controls.update();
        } else {
          t += FOCUS_CAMERA_SPEED;
          requestAnimationFrame(move);
        }
      }
    };

    if (durationFrames === 0) {
      // Execute synchronously for instant teleport to avoid race conditions where
      // controls are replaced between the save and the async restore.
      move();
    } else {
      requestAnimationFrame(move);
    }
  }

  zoomToPoint(
    targetEndX: number,
    targetEndY: number,
    targetEndZ: number,
    cameraEndX: number,
    cameraEndY: number,
    cameraEndZ: number,
    zoomEnd: number,
    teleport: boolean = false,
  ) {
    if (teleport) {
      this.camera.zoom = zoomEnd;
      this.camera.position.set(cameraEndX, cameraEndY, cameraEndZ);
      this.controls.target.set(targetEndX, targetEndY, targetEndZ);
      this.camera.updateProjectionMatrix();
      this.controls.update();
    } else {
      const zoomStart = this.camera.zoom;
      const { x: cameraStartX, y: cameraStartY, z: cameraStartZ } = this.camera.position.clone();
      const { x: targetStartX, y: targetStartY, z: targetStartZ } = this.controls.target.clone();
      const FOCUS_CAMERA_SPEED = 1 / 11;

      let t = FOCUS_CAMERA_SPEED; // Incrementing t before the first frame is drawn makes the transition more responsive.
      const smoothstep = (t: number) => t * t * (3.0 - 2.0 * t);
      const move = () => {
        const ts = smoothstep(Math.min(t, 1.0));

        const ts1 = 1.0 - ts;

        this.camera.zoom = zoomStart * ts1 + ts * zoomEnd;

        this.camera.position.set(
          cameraStartX * ts1 + ts * cameraEndX,
          cameraStartY * ts1 + ts * cameraEndY,
          cameraStartZ * ts1 + ts * cameraEndZ,
        );

        this.controls.target.set(
          targetStartX * ts1 + ts * targetEndX,
          targetStartY * ts1 + ts * targetEndY,
          targetStartZ * ts1 + ts * targetEndZ,
        );

        this.camera.updateProjectionMatrix();
        this.controls.update();
        this.markRendererForUpdate();

        if (t >= 1.0) {
          return;
        }

        t += FOCUS_CAMERA_SPEED;

        requestAnimationFrame(move);
      };

      requestAnimationFrame(move);
    }
  }

  /**
   * Transitions the orthographic camera between two view angles by interpolating
   * spherical coordinates (azimuth, polar), the target position, and optionally zoom.
   * Caller must ensure camera is OrthographicCamera and controls are OrthographicControls.
   * Controls are disabled for the duration; only one transition runs at a time (new one cancels previous).
   */
  transitionOrthographicCameraAngles(
    targetEndX: number,
    targetEndY: number,
    targetEndZ: number,
    distance: number,
    startAzimuth: number,
    startPolar: number,
    endAzimuth: number,
    endPolar: number,
    options: {
      durationFrames?: number;
      easing?: (t: number) => number;
      onComplete?: () => void;
      zoomEnd?: number;
    } = {},
  ): void {
    if (!(this.camera instanceof THREE.OrthographicCamera) || !(this.controls instanceof OrthographicControls)) {
      return;
    }

    const { durationFrames = 11, easing = (t: number) => t * t * (3 - 2 * t), onComplete, zoomEnd } = options;

    const lockId = `transitionOrtho-${newUUID()}`;
    this.controls.lock(lockId);
    this.controls.update();

    const generation = ++this.transitionAnimationGeneration;
    const startTarget = this.controls.target.clone();
    const startZoom = this.camera.zoom;

    const apply = (azimuth: number, polar: number, tgtX: number, tgtY: number, tgtZ: number, zoom: number) => {
      const { dirX, dirY, dirZ } = orthoSphericalToViewDirection(azimuth, polar);
      this.camera.position.set(tgtX - distance * dirX, tgtY - distance * dirY, tgtZ - distance * dirZ);
      this.controls.target.set(tgtX, tgtY, tgtZ);
      this.camera.zoom = zoom;
      this.camera.updateProjectionMatrix();
      this.controls.update();
      this.markRendererForUpdate();
    };

    if (durationFrames <= 0) {
      const endZoom = zoomEnd !== undefined ? zoomEnd : startZoom;
      apply(endAzimuth, endPolar, targetEndX, targetEndY, targetEndZ, endZoom);
      this.controls.unlock(lockId);
      this.controls.update();
      onComplete?.();
      return;
    }

    const step = 1 / durationFrames;
    let t = step;

    const move = () => {
      // If a newer transition animation has started, abandon this animation.
      // The newer animation will restore the controls state.
      if (generation !== this.transitionAnimationGeneration) {
        this.controls.unlock(lockId);
        return;
      }

      const tEased = easing(Math.min(t, 1));
      const azimuth = lerpRadians(startAzimuth, endAzimuth, tEased);
      const polar = startPolar + tEased * (endPolar - startPolar);
      const tgtX = startTarget.x + tEased * (targetEndX - startTarget.x);
      const tgtY = startTarget.y + tEased * (targetEndY - startTarget.y);
      const tgtZ = startTarget.z + tEased * (targetEndZ - startTarget.z);
      const zoom = zoomEnd !== undefined ? startZoom + tEased * (zoomEnd - startZoom) : startZoom;
      apply(azimuth, polar, tgtX, tgtY, tgtZ, zoom);

      if (t >= 1) {
        this.controls.unlock(lockId);
        this.controls.update();
        onComplete?.();
        return;
      }
      t += step;
      requestAnimationFrame(move);
    };

    requestAnimationFrame(move);
  }

  private get raycastScene(): THREE.Scene {
    return this.scene.scene;
  }

  private filterStaticContentFromRaycast(ints: THREE.Intersection[]): THREE.Intersection[] {
    return ints.filter((x) => this.scene.staticContent.children.indexOf(x.object) === -1);
  }

  private clientToNdcCoordinates(clientX: number, clientY: number): [number, number] {
    if (!this.container) {
      return [0, 0];
    }

    const rect = this.container.getBoundingClientRect();

    const ndcCoordinatesX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcCoordinatesY = -((clientY - rect.top) / rect.height) * 2 + 1;
    return [ndcCoordinatesX, ndcCoordinatesY];
  }

  raycast(clientX: number, clientY: number, objects?: THREE.Object3D[]): THREE.Intersection[] {
    if (!this.container) {
      return [];
    }

    const [ndcCoordinatesX, ndcCoordinatesY] = this.clientToNdcCoordinates(clientX, clientY);

    const ints = this.raycastHelper.raycastFromCursor(
      ndcCoordinatesX,
      ndcCoordinatesY,
      this.camera,
      objects ?? this.raycastScene.children,
    );
    return this.filterStaticContentFromRaycast(ints);
  }

  raycastForElevationOcclusion(
    x: number,
    y: number,
    z: number,
    directionX: number,
    directionY: number,
    directionZ: number,
    rayLengthSquared: number,
    nonSceneOcclusionGeometry: THREE.Object3D[],
  ) {
    return this.raycastHelper.raycastForElevationOcclusion(
      nonSceneOcclusionGeometry,
      x,
      y,
      z,
      directionX,
      directionY,
      directionZ,
      rayLengthSquared,
    );
  }

  raycastWorld(x: number, y: number, z: number, directionX: number, directionY: number, directionZ: number) {
    const ints = this.raycastHelper.raycastWorld(this.raycastScene, x, y, z, directionX, directionY, directionZ);
    return this.filterStaticContentFromRaycast(ints);
  }

  raycastWorldTowardsCamera(x: number, y: number, z: number, camera: THREECamera) {
    const ints = this.raycastHelper.raycastWorldTowardsCamera(this.raycastScene, x, y, z, camera);
    return this.filterStaticContentFromRaycast(ints);
  }

  private projectionReferencePlane: THREEPlane = new THREEPlane();

  projectPointToWorld(clientX: number, clientY: number, planeBSP: PlaneBSP): THREE.Vector3 | null {
    if (!this.container) {
      return null;
    }
    this.projectionReferencePlane.constant = planeBSP.dist;
    this.projectionReferencePlane.normal.set(-planeBSP.nX, -planeBSP.nY, -planeBSP.nZ);
    const [ndcCoordinatesX, ndcCoordinatesY] = this.clientToNdcCoordinates(clientX, clientY);
    return this.raycastHelper.raycastFromCursorToPlane(
      ndcCoordinatesX,
      ndcCoordinatesY,
      this.camera,
      this.projectionReferencePlane,
    );
  }

  projectPointToScreen(x: number, y: number, z: number, toClientCoords: boolean = false): number[] {
    if (!this.container) {
      return [0, 0];
    }

    const pos = new THREE.Vector3(x, y, z);
    pos.project(this.camera);

    const rect = this.container.getBoundingClientRect();

    const widthHalf = rect.width / 2;
    const heightHalf = rect.height / 2;

    pos.x = pos.x * widthHalf + widthHalf;
    pos.y = -(pos.y * heightHalf) + heightHalf;

    if (toClientCoords) {
      pos.x += rect.left;
      pos.y += rect.top;
    }

    return [pos.x, pos.y];
  }

  /**
   * Return the current canvas contents as an image. For generating high-quality 3D screenshots, you should use screenshotOfBuilding instead.
   */
  async renderScreenshot(type: "image/png" | "image/jpeg" = "image/png", quality: number = 1): Promise<Blob | null> {
    return this.viewerRenderer.renderScreenshot(type, quality);
  }

  onResize() {
    this.viewerRenderer?.onResize();
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = this.getAspectRatio();
      this.camera.updateProjectionMatrix();
    } else if (this.camera instanceof THREE.OrthographicCamera) {
      const aspect = this.getAspectRatio();
      const height = this.camera.top - this.camera.bottom;
      const width = aspect * height;
      this.camera.left = -width / 2;
      this.camera.right = width / 2;
      this.camera.updateProjectionMatrix();
    }
    this.controls.update(); // Max distance may have changed.
  }

  requestResize() {
    this.viewerRenderer?.requestResize();
  }

  /**
   * This should be called when the rendered image should be updated.
   * It's automatically called when the camera or scene is changed using the utility functions in this class.
   * In case the scene is mutated in some other way, it's better to call SSFXRendererScene.markRenderingNeedsUpdate() if possible.
   */
  markRendererForUpdate() {
    this.viewerRenderer?.markRendererForUpdate();
  }

  // Absent renderer counts as settled so a settle-poll can't hang on a disposed viewer.
  isRenderSettled(): boolean {
    return this.viewerRenderer?.isRenderSettled() ?? true;
  }

  set animate(value: boolean) {
    this.baseOptions.animate = value;
    if (this.viewerRenderer) {
      this.viewerRenderer.setAnimate(value);
    }
  }

  get animate(): boolean {
    return this.baseOptions.animate;
  }

  applyAnimation(): void {
    if (this.controls && this.controls.autoRotate) {
      this.controls.update();
    }
  }

  /**
   * @param background Background color in light mode.
   * @param darkModeBackground The background as it should appear in dark mode (after rest of the colors are inverted).
   * The value is quantized to inverse SRGB space, so it is not fully accurate for dark colors. By default dark mode uses inverted light mode background.
   */
  setSceneBackground(background: THREE.Color | null, darkModeBackground?: THREE.Color | null) {
    this.scene.setBackground(background, darkModeBackground);
    this.scene.markRenderingNeedsUpdate();
  }

  set grid2DVisible(value: boolean) {
    this.scene.grid2DVisible = value;
  }

  clear() {
    this.scene.clear(false);
  }

  /**
   * @param overlaySceneId Overlay scene id. Leaving the parameter as undefined will add to the base scene.
   */
  add(o: THREE.Object3D, overlaySceneId?: string): THREE.Object3D {
    return this.scene.add(o, overlaySceneId);
  }

  /**
   * @param overlaySceneId Overlay scene id. Leaving the parameter as undefined will remove from the base scene.
   */
  remove(o: THREE.Object3D, overlaySceneId?: string) {
    this.scene.remove(o, overlaySceneId);
  }

  addCameraChangeListener(listener: () => void): void {
    this.cameraChangeListeners.push(listener);
  }

  removeCameraChangeListener(listener: () => void): void {
    const index = this.cameraChangeListeners.indexOf(listener);
    if (index >= 0) {
      this.cameraChangeListeners.splice(index, 1);
    }
  }

  onCameraChange() {
    for (const changeListener of this.cameraChangeListeners) {
      changeListener();
    }
  }

  addControlsChangeListener(listener: (controls: CanvasControls) => void): void {
    this.controlsChangeListeners.push(listener);
  }

  removeControlsChangeListener(listener: (controls: CanvasControls) => void): void {
    const index = this.controlsChangeListeners.indexOf(listener);
    if (index >= 0) {
      this.controlsChangeListeners.splice(index, 1);
    }
  }

  private onControlsChange(controls: CanvasControls): void {
    for (const changeListener of this.controlsChangeListeners) {
      changeListener(controls);
    }
  }

  pickEntityIds(clientRect: AABB2): number[] {
    if (!this.viewerRenderer) {
      return [];
    }
    return this.viewerRenderer.pickEntityIds(clientRect);
  }

  prepareRender() {
    this.isolateModeUniform.value = this.scene.highlightDataTexture?.isolateMode ?? false;
  }

  abstract set skyboxVisible(visible: boolean);

  protected abstract initViewerRenderer();
}
